// Console + network ring buffers. Capture the last N lines of console output and
// network errors so a submission carries the context the user was seeing.
//
// Redaction is applied before storage, not after — if we never hold the plaintext
// we can't accidentally leak it in a report. See L-059 and @oncatch/types/redaction.

// Subpath import, NOT the '@oncatch/types' barrel. The barrel re-exports
// webhook-v1/v2, which import Zod — so a barrel import drags Zod's whole
// runtime into the widget bundle for two pure string functions. Zod then
// probes for eval support at startup (`Function("")` in a try/catch),
// which fires a script-src CSP violation on every strict-CSP host page
// (#454). Importing the subpath removes the probe and the weight.
import { redactText, redactUrlQueryValues } from '@oncatch/types/redaction';

const CONSOLE_BUFFER_SIZE = 50;
const NETWORK_BUFFER_SIZE = 25;

export interface ConsoleEntry {
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
  timestamp: string;
}

export interface NetworkErrorEntry {
  url: string;
  method: string;
  status: number | null;
  error?: string;
  timestamp: string;
}

const consoleBuffer: ConsoleEntry[] = [];
const networkBuffer: NetworkErrorEntry[] = [];

let installed = false;
let captureDisabled = false;

export function installBuffers(): void {
  if (installed) return;
  installed = true;
  patchConsole();
  patchFetch();
  patchXhr();
}

/**
 * #56-followup / sensitive-data-mode: stop capturing into the ring buffers
 * AND drop everything currently in them. Patches stay in place (we can't
 * un-patch globals safely from this far up the call stack), but every
 * future push() short-circuits and the existing buffer contents are
 * cleared. Snapshots taken after this fires return empty arrays.
 *
 * Call this from bootstrap when /widget-config returns
 * `sensitiveDataMode: true` — Customer doesn't want OnCatch holding any
 * console / network telemetry from their site, even transiently.
 */
export function disableCapture(): void {
  captureDisabled = true;
  consoleBuffer.length = 0;
  networkBuffer.length = 0;
}

function redactMessage(raw: unknown): string {
  const s = typeof raw === 'string' ? raw : safeStringify(raw);
  return redactText(s);
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function push<T>(buffer: T[], entry: T, max: number) {
  if (captureDisabled) return;
  buffer.push(entry);
  if (buffer.length > max) buffer.shift();
}

function patchConsole() {
  const levels: ConsoleEntry['level'][] = ['log', 'warn', 'error', 'info', 'debug'];
  for (const level of levels) {
    // biome-ignore lint/suspicious/noConsole: widget patches console to capture errors for submission
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      try {
        push(
          consoleBuffer,
          {
            level,
            message: args
              .map((a) => redactMessage(a))
              .join(' ')
              .slice(0, 500),
            timestamp: new Date().toISOString(),
          },
          CONSOLE_BUFFER_SIZE,
        );
      } catch {
        // never let telemetry break the host page
      }
      original(...args);
    };
  }
}

function patchFetch() {
  if (typeof window.fetch !== 'function') return;
  const original = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const started = Date.now();
    try {
      const res = await original(...args);
      if (!res.ok) {
        push(
          networkBuffer,
          {
            url: redactUrlQueryValues(safeUrl(args[0]) ?? ''),
            method: getMethod(args) ?? 'GET',
            status: res.status,
            timestamp: new Date(started).toISOString(),
          },
          NETWORK_BUFFER_SIZE,
        );
      }
      return res;
    } catch (err) {
      push(
        networkBuffer,
        {
          url: redactUrlQueryValues(safeUrl(args[0]) ?? ''),
          method: getMethod(args) ?? 'GET',
          status: null,
          error: err instanceof Error ? err.message.slice(0, 200) : 'network_error',
          timestamp: new Date(started).toISOString(),
        },
        NETWORK_BUFFER_SIZE,
      );
      throw err;
    }
  };
}

function patchXhr() {
  if (typeof XMLHttpRequest === 'undefined') return;
  const OriginalXhr = XMLHttpRequest;
  const origOpen = OriginalXhr.prototype.open;
  const origSend = OriginalXhr.prototype.send;
  OriginalXhr.prototype.open = function patchedOpen(
    this: XMLHttpRequest & { __oncatch_method?: string; __oncatch_url?: string },
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__oncatch_method = method;
    this.__oncatch_url = typeof url === 'string' ? url : url.toString();
    // biome-ignore lint/suspicious/noExplicitAny: underlying signature is variadic
    return origOpen.apply(this, [method, url, ...(rest as any[])] as any);
  };
  OriginalXhr.prototype.send = function patchedSend(
    this: XMLHttpRequest & { __oncatch_method?: string; __oncatch_url?: string },
    ...args: unknown[]
  ) {
    const method = this.__oncatch_method ?? 'GET';
    const url = this.__oncatch_url ?? '';
    const started = Date.now();
    this.addEventListener('loadend', () => {
      if (this.status >= 400 || this.status === 0) {
        push(
          networkBuffer,
          {
            url: redactUrlQueryValues(url),
            method,
            status: this.status || null,
            error: this.status === 0 ? 'network_error_or_blocked' : undefined,
            timestamp: new Date(started).toISOString(),
          },
          NETWORK_BUFFER_SIZE,
        );
      }
    });
    // biome-ignore lint/suspicious/noExplicitAny: variadic send signature
    return origSend.apply(this, args as any);
  };
}

function safeUrl(input: Parameters<typeof fetch>[0]): string | null {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url;
  return null;
}

function getMethod(args: Parameters<typeof fetch>): string | null {
  const init = args[1];
  if (init && typeof init === 'object' && 'method' in init && typeof init.method === 'string') {
    return init.method;
  }
  if (args[0] instanceof Request) return args[0].method;
  return null;
}

export function snapshotConsole(): ConsoleEntry[] {
  return consoleBuffer.slice();
}

export function snapshotNetworkErrors(): NetworkErrorEntry[] {
  return networkBuffer.slice();
}
